import { PrimaryRecordResourceCategory } from '../MedicalDataTypes';
import * as DynamoTypes from '../DynamoTypes';

export type OneUpHealthAPIVersion =
    | 'FHIR STU3 3.0.1'
    | 'FHIR R4 4.0.1'
    | 'FHIR DSTU2 1.0.2'
    | 'FHIR R4 4.0.0'
    | 'FHIR R4';
export type OneUpHealthConnectionStatus = 'connection_working' | 'connection_error';
export type OneUpHealthEHR =
    | 'eClinicalWorks'
    | 'Humana'
    | 'Epic'
    | 'Cerner'
    | '1upHealth'
    | 'Allscripts'
    | ''
    | 'Meditech'
    | 'Edifecs'
    | 'Medicare'
    | '1up'
    | 'Veradigm';

export type OneUpHealthAddress = {
    line: string[];
    city: string | null;
    state: string;
    /**
     * Legacy endpoints (prior to the https://system-search.1up.health/api/all-systems endpoint) did not include postalCode.
     */
    postalCode?: string | null;
};

export type OneUpHealthLocation = {
    name: string;
    address: OneUpHealthAddress;
};

export type OneUpHealthEndpoint = {
    id: number;
    name: string;
    logo: string | null;
    status?: OneUpHealthConnectionStatus;
    ehr: OneUpHealthEHR;
    locations: OneUpHealthLocation[];
    resourceUrl?: string;
    fhirVersions?: OneUpHealthAPIVersion[];
    primaryFhirVersion?: OneUpHealthAPIVersion;
};

export type OneUpHealthEndpointWithDisabledStatus = OneUpHealthEndpoint & {
    isDisabled: boolean;
};

export type OneUpHealthEndpointsSearchRequest = {
    search: string;
};

export type OneUpHealthEndpointsSearchResponse = {
    endpoints: OneUpHealthEndpointWithDisabledStatus[];
};

export type OneUpHealthEndpointResponse = {
    endpoint: OneUpHealthEndpointWithDisabledStatus | null;
};

export type OneUpHealthConnectRequest = {
    healthSystemId: number;
};

export type OneUpHealthConnectResponse = {
    connectUrl: string;
};

export type ExitingRefreshProgress = {
    status: 'progress-record-found';
    progress: DynamoTypes.MedicalProfileRefreshProgress;
    refreshUser: DynamoTypes.User | null;
    isRefreshExpired: boolean;
};

export type NoRefreshProgress = { status: 'no-progress-record-found' };

export type RefreshProgressResponse = ExitingRefreshProgress | NoRefreshProgress;

export type ConnectedHealthSystemResponseItem = {
    endpoint: OneUpHealthEndpointWithDisabledStatus;
    connectedTime: string;
    refreshedTime?: string;
    patientRecordName?: PatientRecordName;
    automaticRefreshExpirationDateTime?: string;
    userDisabledAutomaticRefresh: boolean;
};

export type PendingConnection = Omit<ConnectedHealthSystemResponseItem, 'refreshedTime' | 'patientRecordName'>;

export type ConnectedHealthSystemsResponse = {
    connectedHealthSystems: ConnectedHealthSystemResponseItem[];
    pendingConnections: PendingConnection[];
};

export type PatientRecordName = {
    first?: string;
    middle?: string;
    last?: string;
};

export type PatientMismatchProperty = 'name' | 'dob';

export type ConnectedHealthSystemDetails = {
    name: PatientRecordName | null;
    dob: string | null;
    patientMismatchProperties: PatientMismatchProperty[];
    connectedBy: DynamoTypes.User | null;
    connectedTime: string | null;
    refreshedTime: string | null;
    automaticRefreshExpirationDateTime: string | null;
    endpoint: OneUpHealthEndpointWithDisabledStatus;
    hasMisMatchBeenConfirmed: boolean;
    userDisabledAutomaticRefresh: boolean;
};

export type OneUpDataPullResourceCounts = Record<PrimaryRecordResourceCategory, number>;

export type OneUpDataPullHistoryEntry = {
    dateTime: string;
    status: 'success' | 'error';
    resourceCounts: OneUpDataPullResourceCounts;
};

export type OneUpDataPullHistoryResponse = {
    history: OneUpDataPullHistoryEntry[];
};

export type UpdateUserDisabledAutomaticRefreshRequest = {
    userDisabledAutomaticRefresh: boolean;
};

// Fasten-specific types (independent from OneUp so OneUp code can be removed later)
export type FastenDataPullResourceCounts = Record<PrimaryRecordResourceCategory, number>;

export type FastenDataPullHistoryEntry = {
    dateTime: string;
    status: 'success' | 'error';
    resourceCounts: FastenDataPullResourceCounts;
};

export type FastenDataPullHistoryResponse = {
    history: FastenDataPullHistoryEntry[];
};

export enum FastenWebhookEventTypes {
    patientConnectionSuccess = 'patient.connection_success',
    patientAuthorizationRevoked = 'patient.authorization_revoked',
    patientEhiExportFailed = 'patient.ehi_export_failed',
    patientEhiExportSuccess = 'patient.ehi_export_success',
    webhookTest = 'webhook.test',
}

export type FastenApiMode = 'test' | 'live';
export type FastenConnectionStatus = 'authorized' | 'rejected' | 'refreshing' | 'revoked';

interface BaseFastenWebhookEvent<T extends FastenWebhookEventTypes, D> {
    type: T;
    date: string;
    id: string;
    data: D;
    api_mode: FastenApiMode;
}

interface ConnectionSuccessData {
    org_connection_id: string;
    /** Omitted for TEFCA connections, which have no catalog portal behind them. */
    endpoint_id?: string;
    portal_id?: string;
    brand_id?: string;
    platform_type?: string;
    request_id?: string;
    external_state?: string;
    /** Always supplied on connect; without it the connection cannot be routed to a profile. */
    external_id: string;
    connection_status: FastenConnectionStatus;
    /** Omitted for TEFCA connections, whose authorizations have no standard expiry. */
    consent_expires_at?: string;
    /** TEFCA only. An OID URN such as `urn:oid:2.16.840.1.111111.3.3126.2.3.33929.5`. */
    tefca_directory_id?: string;
    /** TEFCA connections always carry `patient/*.read`. */
    scope?: string;
}

interface AuthorizationRevokedData {
    org_connection_id: string;
    /** Omitted for TEFCA connections. */
    endpoint_id?: string;
    portal_id?: string;
    brand_id?: string;
    platform_type?: string;
    connection_status: 'revoked';
}

interface EhiExportSuccessData {
    download_link: string;
    download_links: {
        url: string;
        export_type: string;
        content_type: string;
    }[];
    org_connection_id: string;
    task_id: string;
    org_id: string;
    stats: {
        total_resources?: number;
        total_by_resource_type?: any;
    };
}

interface EhiExportFailureData {
    failure_reason: string;
    org_connection_id: string;
    task_id: string;
    org_id: string;
}

export type FastenWebhookConnectionSuccessEvent = BaseFastenWebhookEvent<
    FastenWebhookEventTypes.patientConnectionSuccess,
    ConnectionSuccessData
>;

export type FastenWebhookAuthorizationRevokedEvent = BaseFastenWebhookEvent<
    FastenWebhookEventTypes.patientAuthorizationRevoked,
    AuthorizationRevokedData
>;

export type FastenWebhookEhiExportSuccessEvent = BaseFastenWebhookEvent<
    FastenWebhookEventTypes.patientEhiExportSuccess,
    EhiExportSuccessData
>;

export type FastenWebhookEhiExportFailedEvent = BaseFastenWebhookEvent<
    FastenWebhookEventTypes.patientEhiExportFailed,
    EhiExportFailureData
>;

export type FastenWebhookTestEvent = BaseFastenWebhookEvent<
    FastenWebhookEventTypes.webhookTest,
    Record<string, unknown>
>;

export type FastenWebhookEvent =
    | FastenWebhookConnectionSuccessEvent
    | FastenWebhookAuthorizationRevokedEvent
    | FastenWebhookEhiExportSuccessEvent
    | FastenWebhookEhiExportFailedEvent
    | FastenWebhookTestEvent;

// Fasten Catalog Search
export type FastenCatalogSearchRequest = {
    search: string;
    /** Sandbox opt-in. Honoured only outside prod and only for staff; absent means live. */
    apiMode?: FastenApiMode;
};

// Fasten Connect
export type FastenConnectRequest = {
    brandId: string;
    portalId: string;
    endpointId: string;
    /** Sandbox opt-in. Honoured only outside prod and only for staff; absent means live. */
    apiMode?: FastenApiMode;
};

/** Response of POST /ehr-integrations/fasten/tefca-session/:medicalProfileId. Sandbox mode only. */
export type FastenTefcaSessionResponse = {
    /** Fasten's sandbox public id. The key decides the mode, so this is the whole mode contract. */
    publicId: string;
    /** `${medicalProfileId}_${userId}`; Fasten echoes it back on patient.connection_success. */
    externalId: string;
};

export type FastenCatalogSearchEntry = {
    brand_id: string;
    name: string;
    aliases: string[];
    platform_type: string[];
    locations: string[];
    brand_website?: string;
    portals: {
        id: string;
        name: string;
        endpoints: {
            id: string;
            platform_type: string;
        }[];
    }[];
};

export type FastenCatalogSearchResponse = {
    entries: FastenCatalogSearchEntry[];
};

export type FastenBulkRecordsResponse = {
    success: boolean;
    data: {
        task_id: string;
        status: 'success' | 'pending' | 'failed';
    };
};
